"use client";

import * as stylex from "@stylexjs/stylex";
import { usePathname, useRouter } from "next/navigation";
import { type Dispatch, type ReactNode, type RefObject, useEffect, useReducer, useRef, useState } from "react";
import { flushSync } from "react-dom";
import { idleDue } from "@/domain/idle";
import "@/theme/tokens.stylex";
import { colors, motion, sizes, space } from "@/theme/tokens.stylex";
import { useFieldInView } from "@/ui/fieldInView";
import { PhotoBackdrop } from "@/ui/PhotoBackdrop";
import { photoLayer } from "@/ui/photoLayer";
import { useBackgroundImage } from "./background";
import { useBreakpoint } from "./breakpoint";
import { isTextField, useKeyboardInset } from "./keyboard";
import { OfflineNotice } from "./OfflineNotice";
import { useNowStore } from "./providers/now";
import { useAccountSettings } from "./providers/settings";
import { VIEWS, type View, viewForPath, viewPath } from "./routes";
import { useSheetHost } from "./SheetHost";
import { SidebarDrawer } from "./SidebarDrawer";
import { SidebarGrip } from "./SidebarGrip";
import { SidebarHandle } from "./SidebarHandle";
import { SidebarRail } from "./SidebarRail";
import { initialSidebar, SIDEBAR_IDLE_SECONDS, type SidebarAction, sidebarInset, sidebarReducer } from "./sidebar";
import { useAmbientNavigate, useAmbientRoot } from "./useAmbientNavigate";
import { usePageTransition } from "./usePageTransition";
const ACTIVITY_EVENTS = ["pointerdown", "keydown", "focusout"] as const;
const styles = {
  root: {
    kZKoxP: "x1dr59a3",
    kB7OPa: "x9f619",
    k1xSpc: "x78zum5",
    kXwgrk: "xdt5ytf",
    kVQacm: "xb3r6kr",
    kVAEAm: "x1n2onr6",
    kHBbk8: "xc8icb0",
    kWkggS: "x1djq8kx",
    kMwMTN: "x1a11ngh",
    k1ekBW: "xt82zda",
    kIyJzY: "xr58nyc",
    kAMwcw: "x19v9z1f",
    $$css: true
  },
  aboveKeyboard: (inset: number) => [{
    kGO01o: inset != null ? "xjqpyvm" : inset,
    $$css: true
  }, {
    "--x-paddingBottom": (val => typeof val === "number" ? val + "px" : val != null ? val : undefined)(inset)
  }],
  besideSidebar: (inset: number) => [{
    kE3dHu: inset != null ? "x13e46o5" : inset,
    $$css: true
  }, {
    "--x-paddingLeft": (val => typeof val === "number" ? val + "px" : val != null ? val : undefined)(inset)
  }]
};
export function AppShell({
  children
}: {
  children: ReactNode;
}) {
  const pathname = usePathname();
  const navigate = useAmbientNavigate();
  const breakpoint = useBreakpoint();
  const shell = useRef<HTMLDivElement>(null);
  const main = usePageTransition<HTMLElement>(pathname);
  const backdrop = useBackgroundImage();
  const view = viewForPath(pathname);
  const keyboardInset = useKeyboardInset();
  const sheetOpen = useSheetHost().sheet !== null;
  const {
    hideSidebarWhenIdle
  } = useAccountSettings();
  const small = breakpoint === "small";
  const [sidebar, dispatch] = useReducer(sidebarReducer, undefined, () => initialSidebar(view, small));
  const handle = useRef<HTMLButtonElement>(null);
  const grip = useRef<HTMLButtonElement>(null);
  const mode = small && sidebar.mode === "rail" ? "hidden" : sidebar.mode;
  const inset = sidebarInset(mode, small);
  const back = {
    type: "back",
    small
  } as const;
  const settled = small ? grip : handle;
  useAmbientRoot(shell, pathname);
  useRailOnViewChange(pathname, view, small, dispatch);
  useEscapeToRail(mode === "expanded", small, dispatch, settled);
  useHideWhenIdle(hideSidebarWhenIdle && mode !== "hidden" && !sheetOpen && keyboardInset === 0, dispatch);
  usePrefetchViews();
  const keyboardPadding = useFieldInView(main, keyboardInset);
  return <div ref={shell} {...stylex.props(styles.root, styles.aboveKeyboard(keyboardPadding), styles.besideSidebar(inset))}>
      <PhotoBackdrop image={backdrop && photoLayer(backdrop.dim, backdrop.src)} />
      <main ref={main} className="x1iyjqo2 x2lwn1j xysyzu8 x9f619 x1243p14 x1quw8nh x2xw44r">
        {children}
      </main>
      <SidebarGrip ref={grip} small={small} shown={mode === "hidden"} onShow={() => small ? dispatch({
      type: "show",
      small
    }) : dispatchThenFocus(dispatch, handle, {
      type: "show"
    })} />
      {!small && <>
          <SidebarRail mode={mode} active={view} onHide={() => dispatchThenFocus(dispatch, grip, {
        type: "hide"
      })} onNavigate={navigate} />
          <SidebarHandle ref={handle} shown={mode !== "hidden"} expanded={mode === "expanded"} onOpen={() => dispatch({
        type: "open"
      })} />
        </>}
      <SidebarDrawer open={mode === "expanded"} small={small} active={view} onClose={() => dispatchThenFocus(dispatch, grip, {
      type: "close"
    })} onScrimTap={() => dispatchThenFocus(dispatch, settled, back)} onNavigate={path => {
      dispatchThenFocus(dispatch, settled, back);
      navigate(path);
    }} />
      <OfflineNotice breakpoint={breakpoint} inset={inset} onWeather={view === "weather"} />
    </div>;
}
function usePrefetchViews() {
  const router = useRouter();
  useEffect(() => {
    for (const view of VIEWS) router.prefetch(viewPath(view));
  }, [router]);
}
function useRailOnViewChange(pathname: string, view: View | undefined, small: boolean, dispatch: Dispatch<SidebarAction>) {
  const [shownOn, setShownOn] = useState(pathname);
  if (pathname !== shownOn) {
    setShownOn(pathname);
    dispatch({
      type: "viewChanged",
      view,
      small
    });
  }
}
function useEscapeToRail(expanded: boolean, small: boolean, dispatch: Dispatch<SidebarAction>, settled: RefObject<HTMLButtonElement | null>) {
  useEffect(() => {
    if (!expanded) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") dispatchThenFocus(dispatch, settled, {
        type: "back",
        small
      });
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [expanded, small, dispatch, settled]);
}
function useHideWhenIdle(running: boolean, dispatch: Dispatch<SidebarAction>) {
  const store = useNowStore();
  useEffect(() => {
    if (!running) return;
    let lastActivity = store.get();
    const touched = () => {
      lastActivity = store.get();
    };
    const onTick = () => {
      const now = store.get();
      if (isTextField(document.activeElement)) lastActivity = now;
      if (idleDue(lastActivity, now, SIDEBAR_IDLE_SECONDS)) dispatch({
        type: "idle"
      });
    };
    const stopWatching = watchActivity(touched);
    const unsubscribe = store.subscribe(onTick);
    return () => {
      stopWatching();
      unsubscribe();
    };
  }, [running, store, dispatch]);
}
function watchActivity(touched: () => void): () => void {
  for (const type of ACTIVITY_EVENTS) document.addEventListener(type, touched, true);
  return () => {
    for (const type of ACTIVITY_EVENTS) document.removeEventListener(type, touched, true);
  };
}
function dispatchThenFocus(dispatch: Dispatch<SidebarAction>, target: RefObject<HTMLButtonElement | null>, action: SidebarAction) {
  flushSync(() => dispatch(action));
  target.current?.focus();
}